import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase/client';
import { refreshSession } from '../lib/auth/session';

interface UseAuthenticationReturn {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  error: Error | null;
}

export const useAuthentication = (): UseAuthenticationReturn => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      try {
        setIsLoading(true);
        
        if (!supabase) {
          throw new Error('Supabase client not properly initialized');
        }
        
        // Get current session
        const { data } = await supabase.auth.getSession();
        setSession(data.session);
        setUser(data.session?.user || null);
        
        // Set up auth state change listener
        const { data: authListener } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            setSession(session);
            setUser(session?.user || null);
            
            // If session is expired, try to refresh
            if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
              setSession(session);
              setUser(session?.user || null);
            } else if (event === 'SIGNED_OUT') {
              setSession(null);
              setUser(null);
            }
          }
        );
        
        // Auto-refresh session when it's nearing expiration
        if (data.session) {
          const expiresAt = data.session.expires_at || 0;
          const expiresIn = expiresAt - Math.floor(Date.now() / 1000);
          
          // If session expires in less than 5 minutes, refresh it
          if (expiresIn < 300) {
            const refreshedSession = await refreshSession();
            setSession(refreshedSession);
            setUser(refreshedSession?.user || null);
          }
        }
        
        setIsLoading(false);
        
        // Clean up auth listener on unmount
        return () => {
          authListener.subscription.unsubscribe();
        };
      } catch (err) {
        console.error('Error initializing auth:', err);
        setError(err instanceof Error ? err : new Error('Failed to initialize authentication'));
        setIsLoading(false);
      }
    };
    
    initAuth();
  }, []);

  return { user, session, isLoading, error };
}; 